// src/components/CameraCapture.tsx

import { useEffect, useRef, useState } from 'react';
import { ERROR_MESSAGES } from '../config/constants';

interface CameraCaptureProps {
  onStreamReady: (stream: MediaStream, video: HTMLVideoElement) => void;
  onError: (error: string) => void;
}

export function CameraCapture({ onStreamReady, onError }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect if device is mobile
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    };
    setIsMobile(checkMobile());
  }, []);

  const startCamera = async (selectedFacingMode: 'user' | 'environment' = facingMode) => {
    setIsLoading(true);

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: selectedFacingMode,
          frameRate: { ideal: 30, max: 30 },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;

        // Wait for video to be ready
        await new Promise<void>((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => {
              videoRef.current!.play();
              resolve();
            };
          }
        });

        setStream(mediaStream);
        onStreamReady(mediaStream, videoRef.current);
      }
    } catch (error: any) {
      console.error('Camera initialization error:', error);

      if (error.name === 'NotAllowedError') {
        onError(ERROR_MESSAGES.cameraPermissionDenied);
      } else if (error.name === 'NotFoundError') {
        onError(ERROR_MESSAGES.cameraNotFound);
      } else {
        onError(ERROR_MESSAGES.cameraInitFailed);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup: stop camera stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const switchCamera = async () => {
    // Stop current stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }

    // Switch facing mode
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);

    // Start camera with new facing mode
    await startCamera(newFacingMode);
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full rounded-lg bg-black"
      />

      {!stream && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gray-900 rounded-lg p-4">
          {isLoading ? (
            <p className="text-white">Starting camera...</p>
          ) : (
            <>
              {isMobile && (
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => {
                      setFacingMode('user');
                    }}
                    className={`px-4 py-2 rounded-lg transition ${
                      facingMode === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300'
                    }`}
                  >
                    📱 Front Camera
                  </button>
                  <button
                    onClick={() => {
                      setFacingMode('environment');
                    }}
                    className={`px-4 py-2 rounded-lg transition ${
                      facingMode === 'environment'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300'
                    }`}
                  >
                    📷 Back Camera
                  </button>
                </div>
              )}
              <button
                onClick={() => startCamera(facingMode)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Start Camera
              </button>
            </>
          )}
        </div>
      )}

      {stream && isMobile && (
        <button
          onClick={switchCamera}
          className="absolute top-4 right-4 px-4 py-2 bg-gray-800 bg-opacity-80 text-white rounded-lg hover:bg-opacity-100 transition"
        >
          🔄 Switch Camera
        </button>
      )}
    </div>
  );
}
